import { normaliseSearch } from '@/features/builder/indicatorSearch';

/**
 * The instrument pickers' search (docs/equities_design.md §10.2): a substring match over the symbol,
 * the display name and the curated `search_terms`, so "nasdaq" finds QQQ and "cable" finds GBP/USD.
 * One helper so every picker on the web matches alike — the phone's `matchesInstrument` is its twin.
 *
 * **Order, strongest first:** an exact symbol, then a symbol prefix, then available before locked,
 * then alphabetical — so "aapl" puts Apple first however many names contain the letters. The
 * indicator picker's tiered ranker is the upgrade if a real query misranks (§21); not before.
 */
export interface SearchableInstrument {
  symbol: string;
  name: string;
  search_terms?: string[] | null;
  available?: boolean | null;
}

/** Whether `query` matches the instrument. An empty query matches everything. */
export function matchesInstrument(instrument: SearchableInstrument, query: string): boolean {
  const q = normaliseSearch(query);
  if (!q) return true;
  if (normaliseSearch(instrument.symbol).includes(q) || normaliseSearch(instrument.name).includes(q)) return true;
  return (instrument.search_terms ?? []).some((t) => normaliseSearch(t).includes(q));
}

/** 0 exact symbol, 1 symbol prefix, 2 anything else. */
function symbolRank(instrument: SearchableInstrument, q: string): number {
  if (!q) return 2;
  const sym = normaliseSearch(instrument.symbol);
  return sym === q ? 0 : sym.startsWith(q) ? 1 : 2;
}

/** A comparator for instruments that already match `query`, in the order above. */
export function compareInstrumentMatches(query: string) {
  const q = normaliseSearch(query);
  return (a: SearchableInstrument, b: SearchableInstrument): number =>
    symbolRank(a, q) - symbolRank(b, q) ||
    Number(b.available !== false) - Number(a.available !== false) ||
    a.symbol.localeCompare(b.symbol);
}

/** The instruments matching `query`, strongest first. */
export function searchInstruments<T extends SearchableInstrument>(instruments: T[], query: string): T[] {
  return instruments.filter((i) => matchesInstrument(i, query)).sort(compareInstrumentMatches(query));
}
